/**
 * Client-side back-off after a 429, so a rate-limited endpoint is asked once
 * per server window instead of once per navigation.
 *
 * WHY THIS EXISTS
 *
 * The auth gate in App.tsx verifies the token with GET /auth/me before a
 * protected route renders. A transient failure (429, 5xx, network) is left
 * unverified on purpose — a blip must never log anyone out — and the effect
 * re-runs on every `location.pathname`. So once the 100 req/60s limiter
 * tripped, every navigation re-fired /auth/me, drew another 429 and left the
 * token unverified again: the retry fed the limit that caused it. Measured on
 * production 2026-08-29 (#589): 33 /auth/me across 8 navigations, 20 of 137
 * requests 429, 11 of 19 authenticated pages degraded.
 *
 * The same shape held for the refresh token: with no access token but a
 * stored refresh token, the gate calls POST /auth/refresh-token on every
 * navigation, public pages included. A 429 there repeated forever, and a dead
 * refresh token (401) was kept and replayed forever.
 *
 * THE RULE
 *
 * When the server answers the SESSION CHECK or the REFRESH with a 429 and a
 * retry hint, that request is not sent again until the hint has elapsed;
 * callers get the same 429 locally. Nothing is retried on anyone's behalf —
 * this only stops re-asking a question the server has already refused. A 429
 * with no hint is not held (a plan cap is not a timed window).
 *
 * Deliberately NOT every endpoint: the limiter's `retryAfter` is always the
 * full window, while its sliding log frees capacity sooner, so a blanket hold
 * would turn a page's Retry button into a no-op for up to a minute. Only the
 * requests the gate re-fires by itself on every navigation are held.
 */

/** A hint above this is clamped: a bad header must not wedge a page for an hour. */
export const MAX_BACKOFF_S = 300;

const heldUntil = new Map<string, number>();

export function backoffKey(method: string | undefined, endpoint: string): string {
  return `${(method ?? 'GET').toUpperCase()} ${endpoint}`;
}

/** The requests the auth gate fires on its own, once per navigation. */
const GATE_REQUESTS: ReadonlySet<string> = new Set(['GET /auth/me', 'POST /auth/refresh-token']);

export function isHeldOn429(key: string): boolean {
  return GATE_REQUESTS.has(key);
}

/**
 * Seconds the server asked us to wait, or null when it gave no usable hint.
 * The JSON body's `retryAfter` comes first: the API is cross-origin and does
 * not expose `Retry-After` to the browser, so the header is a fallback only.
 */
export function retryAfterSeconds(body: unknown, headers?: Headers): number | null {
  const fromBody = typeof body === 'object' && body !== null
    ? Number((body as Record<string, unknown>).retryAfter)
    : NaN;
  const fromHeader = Number(headers?.get?.('Retry-After') ?? NaN);
  const seconds = Number.isFinite(fromBody) && fromBody > 0 ? fromBody
    : Number.isFinite(fromHeader) && fromHeader > 0 ? fromHeader
    : null;
  return seconds === null ? null : Math.min(Math.ceil(seconds), MAX_BACKOFF_S);
}

/** Hold `key` for `seconds` from `now`. */
export function holdFor(key: string, seconds: number, now: number = Date.now()): void {
  heldUntil.set(key, now + seconds * 1000);
}

/** Whole seconds `key` is still held for; 0 when it may be sent. */
export function heldSeconds(key: string, now: number = Date.now()): number {
  const until = heldUntil.get(key);
  if (until === undefined) return 0;
  if (until <= now) {
    heldUntil.delete(key);
    return 0;
  }
  return Math.ceil((until - now) / 1000);
}

/** Test seam: forget every hold. */
export function resetBackoff(): void {
  heldUntil.clear();
}
